import React, {useState} from 'react';
import ArrModal from '../../../../common/arr-modal/ArrModal';
import {Box, IconButton, Typography} from '@mui/material';
import ArrButton from '../../../../common/arr-button/ArrButton';
import {t} from 'i18next';
import './ConfirmModal.scss';
import ArrTooltipWrapper from '../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../../assets/images/info-icon.svg';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import dayjs from 'dayjs';
import ArrTextField from '../../../../common/arr-text-field/ArrTextField';
import utc from 'dayjs/plugin/utc';
import {Trans} from 'react-i18next';
interface IConfirmModal {
  isOpen: boolean;
  onClose: () => void;
  selectedDocumentCount: string;
  onConfirm: () => void;
  isLoading?: boolean;
}

const ConfirmModal = (props: IConfirmModal) => {
  dayjs.extend(utc);
  const {
    isOpen,
    onClose,
    selectedDocumentCount,
    onConfirm,
    isLoading = false,
  } = props;
  const [textboxValue, setTextboxValue] = useState<string>('');
  const isConfirmDisabled =
    textboxValue.trim().replace(/\s+/g, ' ') !== 'I understand';
  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );

  const now = dayjs.utc();

  const name = loginUserData?.userName || '';
  const time = now.format('YYYY/MM/DD [at] hh:mm:ss A');

  return (
    <div className="ai-index-confirm-modal" data-testid="confirm-modal-div">
      <ArrModal
        showModal={isOpen}
        onClose={onClose}
        dataTestId="confirm-modal"
        className="ai-index-confirm-modal"
        showClose={false}
        aria-labelledby="confirm-modal-title"
        aria-describedby="confirm-modal-desc-1 confirm-modal-desc-2 confirm-modal-desc-3 confirm-modal-desc-4"
        headerTitleElement={
          <Box className="modal-header">
            <Typography
              id="confirm-modal-title"
              className="typo-text headline2_size modal-text-color"
              data-testid="confirm-title"
            >
              {t('ai.investigate.createEditIndex.modal.header')}
            </Typography>

            <ArrTooltipWrapper
              data-testid="select-document-tooltip-wrapper"
              trigger={
                <IconButton
                  aria-label="info-icon"
                  size="small"
                  data-testid="info-icon"
                  className="info-icon-button"
                >
                  <InfoIcon
                    className="info-icon"
                    aria-hidden="true"
                    focusable="false"
                  />
                </IconButton>
              }
            >
              <Trans
                i18nKey="ai.investigate.createEditIndex.create.selectDocumentsTooltipContent"
                components={{
                  br: <br data-testid="confirm-modal-br" />,
                  strong: <strong data-testid="confirm-modal-strong" />,
                  p: <p data-testid="confirm-modal-p" />,
                }}
              />
            </ArrTooltipWrapper>
          </Box>
        }
      >
        <Box className="modal-content">
          <Box className="modal-sub-header">
            <Typography
              id="confirm-modal-desc-1"
              className="typo-text modal-text-color body2_size"
              data-testid="confirm-modal-body-title"
            >
              {t('ai.investigate.createEditIndex.modal.bodyTitle')}
            </Typography>
          </Box>

          <Box className="modal-sub-content">
            <Typography
              id="confirm-modal-desc-2"
              component="span"
              className="typo-text modal-text-color body2_size"
              data-testid="confirm-modal-body-docline"
            >
              {t('ai.investigate.createEditIndex.modal.bodyDocline')}&nbsp;
            </Typography>
            <Typography
              id="confirm-modal-desc-3"
              component="span"
              className="typo-text modal-text-color subhead4_size"
              data-testid="confirm-modal-document-count"
            >
              {selectedDocumentCount}
            </Typography>
          </Box>

          <Box className="modal-sub-footer">
            <Typography
              id="confirm-modal-desc-4"
              className="typo-text modal-text-color body2_size"
              data-testid="confirm-modal-confirm-message"
            >
              {t('ai.investigate.createEditIndex.modal.confirmMessage')}
            </Typography>
          </Box>

          <Box className="modal-content-textbox">
            <ArrTextField
              id="enabled-focus"
              state={
                isConfirmDisabled && textboxValue !== '' ? 'error' : 'enabled'
              }
              aria-label={t(
                'ai.investigate.createEditIndex.modal.inputPlaceHolder'
              )}
              label={t('ai.investigate.createEditIndex.modal.inputPlaceHolder')}
              dataTestId="confirm-modal-placeholder"
              placeholder={''}
              supportText={
                textboxValue === ''
                  ? ''
                  : isConfirmDisabled
                    ? t('ai.investigate.createEditIndex.modal.supportTextError')
                    : t(
                        'ai.investigate.createEditIndex.modal.supportTextSignature',
                        {name, time}
                      )
              }
              value={textboxValue}
              onChange={(e) => {
                setTextboxValue(e.target.value);
              }}
              showErrorIcon
            />
          </Box>

          <Box className="modal-action-buttons">
            <ArrButton
              variant="secondary"
              size="medium"
              onClick={onClose}
              aria-label={`${t('common.cancel')}. ${t(
                'ai.investigate.createEditIndex.modal.cancelButtonHint'
              )}`}
              dataTestId="confirm-modal-cancel-button"
              className="modal-action-button"
            >
              {t('common.cancel')}
            </ArrButton>

            <ArrButton
              isAriaDisabled={isConfirmDisabled}
              variant="primary"
              size="medium"
              type="submit"
              aria-label={`${t(
                'ai.summarize.createJob.confirmModal.confirm'
              )}. ${t('ai.investigate.createEditIndex.modal.confirmButtonHint')}`}
              dataTestId="confirm-modal-confirm-button"
              onClick={onConfirm}
              className="modal-action-button"
              loading={isLoading}
              disabled={isLoading ? true : false}
            >
              {t('ai.summarize.createJob.confirmModal.confirm')}
            </ArrButton>
          </Box>
        </Box>
      </ArrModal>
    </div>
  );
};

export default ConfirmModal;
